import clsx from 'clsx';
import { AlertTriangle, Download, FileImage, FileSpreadsheet, FileText, File as FileIcon, Loader2, Lock } from 'lucide-react';
import type { AttachmentStatus, AttachmentView } from '@mailpilot/shared';
import { bytes, fileKind } from '../../lib/format';
import { downloadFile } from '../../lib/api';
import { useToast } from '../ui/Toast';

export function fileIcon(mime: string, cls = 'h-4 w-4') {
  if (mime.startsWith('image/')) return <FileImage className={cls} aria-hidden />;
  if (/spreadsheet|excel|csv/.test(mime)) return <FileSpreadsheet className={cls} aria-hidden />;
  if (/pdf|word|document|text/.test(mime)) return <FileText className={cls} aria-hidden />;
  return <FileIcon className={cls} aria-hidden />;
}

export const attachmentStatusLabel: Record<AttachmentStatus, { label: string; tone: 'ok' | 'warn' | 'bad' | 'busy' }> = {
  pending: { label: 'Waiting', tone: 'busy' },
  stored: { label: 'Stored', tone: 'ok' },
  parsing: { label: 'Reading…', tone: 'busy' },
  parsed: { label: 'Summarised', tone: 'ok' },
  partial: { label: 'Partially read', tone: 'warn' },
  unsupported: { label: 'Unsupported type', tone: 'warn' },
  encrypted: { label: 'Encrypted', tone: 'warn' },
  unreadable: { label: 'Unreadable', tone: 'bad' },
  too_large: { label: 'Too large to read', tone: 'warn' },
  failed: { label: 'Processing failed', tone: 'bad' },
};

/** Compact file chip: icon tile, name, kind · size, and a download affordance revealed on hover. */
export function AttachmentChip({ a }: { a: AttachmentView }) {
  const toast = useToast();
  const st = attachmentStatusLabel[a.status];
  return (
    <button
      type="button"
      onClick={() => downloadFile(`/api/attachments/${a.id}/download`, a.filename).catch((e) => toast.error(e))}
      className="group inline-flex max-w-full items-center gap-2.5 rounded-xl border border-hairline bg-surface py-1.5 pl-1.5 pr-3 text-left transition-colors hover:border-line hover:bg-raised"
      title={`Download ${a.filename}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-primary-soft text-primary-ink">
        {a.status === 'encrypted' ? <Lock className="h-4 w-4" aria-hidden /> : fileIcon(a.mimeType)}
      </span>
      <span className="min-w-0">
        <span className="block max-w-[14rem] truncate text-[0.8125rem] font-medium leading-snug text-ink">{a.filename}</span>
        <span className="flex items-center gap-1 text-[0.6875rem] leading-snug text-muted">
          {fileKind(a.mimeType, a.filename)} · {bytes(a.size)}
          {st.tone !== 'ok' && (
            <span className={clsx('inline-flex items-center gap-0.5', st.tone === 'bad' ? 'text-danger-ink' : st.tone === 'warn' ? 'text-warn-ink' : '')}>
              · {st.tone === 'busy' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <AlertTriangle className="h-3 w-3" aria-hidden />} {st.label}
            </span>
          )}
        </span>
      </span>
      <Download className="h-4 w-4 shrink-0 text-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
    </button>
  );
}
